import {
  Badge,
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  ProgressBar,
  Spinner,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useDatasetVersions } from '../../../hooks/useDatasetVersions/useDatasetVersions.js';
import type {
  DataEntry,
  DataFile,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import {
  USAGE_EXTRA_KEYS,
  withCorrelationId,
} from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';
import { buildZipBlob } from '../../../utils/zipBuilder.js';

import type {
  DownloadDatasetDialogProps,
  DownloadStage,
} from './DownloadDatasetDialog.interface.js';
import { useStyles } from './DownloadDatasetDialog.style.js';

const STAGE_LABELS: Record<DownloadStage, string> = {
  confirm: '',
  'listing-entries': 'Listing data entries...',
  'getting-urls': 'Getting download URLs...',
  downloading: 'Downloading files...',
  'building-zip': 'Building zip file...',
  done: 'Download complete',
  error: 'Download failed',
};

const LIST_PAGE_SIZE = 100;
const DOWNLOAD_BATCH_SIZE = 20;

function sanitizeFileName(name: string): string {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_');
}

function statusColor(status: string) {
  return status === 'Published'
    ? ('success' as const)
    : status === 'Draft'
      ? ('important' as const)
      : ('subtle' as const);
}

export function DownloadDatasetDialog({
  open,
  dataSetId,
  currentVersion,
  datasetName,
  onClose,
}: DownloadDatasetDialogProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();
  const abortRef = useRef<AbortController | null>(null);

  const { versions, isLoading: versionsLoading } =
    useDatasetVersions(dataSetId);

  const [selectedVersion, setSelectedVersion] =
    useState<number>(currentVersion);
  const [stage, setStage] = useState<DownloadStage>('confirm');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedVersion(currentVersion);
    }
  }, [open, currentVersion]);

  const selectedVersionInfo = useMemo(
    () => versions.find((v) => v.version === selectedVersion),
    [versions, selectedVersion],
  );

  const latestVersion = useMemo(
    () =>
      versions.length > 0
        ? Math.max(...versions.map((v) => v.version))
        : currentVersion,
    [versions, currentVersion],
  );

  const handleVersionSelect = useCallback(
    (_: unknown, data: { optionValue?: string }) => {
      const val = data.optionValue;
      if (val) {
        setSelectedVersion(Number(val));
      }
    },
    [],
  );

  const handleDownload = useCallback(async () => {
    const abort = new AbortController();
    abortRef.current = abort;
    const downloadVersion = selectedVersion;

    // One operational scenario for the whole download flow. Milestones read as
    // a mini-trace; the correlation GUID rides the single summary TEDH call.
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDownloadDataset,
      { abort },
    );
    const completeAbandoned = (): void => {
      scenario.complete('abandoned', {
        'Usage.ErrorCategory': 'cancelled',
      });
    };

    try {
      setStage('listing-entries');
      setProgress({ current: 0, total: 0 });
      scenario.mark('entries_list_started');

      const allEntries: DataEntry[] = [];
      let continuationToken: string | null = null;
      let pageNumber = 0;

      do {
        if (abort.signal.aborted) {
          completeAbandoned();
          return;
        }
        const result = await service.listDataEntries({
          dataSetId,
          dataSetVersion: downloadVersion,
          pageSize: LIST_PAGE_SIZE,
          continuationToken: continuationToken ?? undefined,
          includeCount: true,
        });
        allEntries.push(...result.items);
        continuationToken = result.continuationToken;
        pageNumber++;
        scenario.mark('entries_page_listed', {
          page_number: pageNumber,
          page_entry_count: result.items.length,
          total_entries: allEntries.length,
        });
        setProgress({
          current: allEntries.length,
          total: result.totalCount ?? allEntries.length,
        });
      } while (continuationToken !== null);

      if (abort.signal.aborted) {
        completeAbandoned();
        return;
      }

      scenario.mark('entries_listed', { entry_count: allEntries.length });

      if (allEntries.length === 0) {
        setErrorMessage('No entries found in this dataset version.');
        setStage('error');
        scenario.complete('failure', { reason: 'no_entries' });
        return;
      }

      setStage('getting-urls');
      setProgress({ current: 0, total: allEntries.length });

      const entryFileUrls = new Map<
        string,
        { entry: DataEntry; files: Record<string, string> }
      >();
      let failedUrlEntries = 0;

      for (let i = 0; i < allEntries.length; i += DOWNLOAD_BATCH_SIZE) {
        if (abort.signal.aborted) {
          completeAbandoned();
          return;
        }
        const batch = allEntries.slice(i, i + DOWNLOAD_BATCH_SIZE);
        const batchNumber = Math.floor(i / DOWNLOAD_BATCH_SIZE) + 1;
        scenario.mark('download_urls_batch_started', {
          batch_number: batchNumber,
          entry_count: batch.length,
        });
        // Record exactly one "Download Dataset" TEDH usage event per download
        // (D2): attach the usage context to the first batch-URL request only,
        // carrying the version, dataset name, total entry count, and the
        // scenario correlation GUID.
        const isFirstBatch = i === 0;
        const urlResult = await service.batchGetDownloadUrls(
          dataSetId,
          String(downloadVersion),
          batch.map((e) => ({ dataId: e.dataId })),
          isFirstBatch
            ? {
                sourceId: String(downloadVersion),
                extras: withCorrelationId(scenario.queryId, {
                  [USAGE_EXTRA_KEYS.DATASET_NAME]: datasetName,
                  [USAGE_EXTRA_KEYS.ENTRY_COUNT]: String(allEntries.length),
                }),
              }
            : undefined,
        );

        for (const item of urlResult.succeededItems) {
          const entry = allEntries.find((e) => e.dataId === item.dataId);
          if (entry) {
            entryFileUrls.set(item.dataId, { entry, files: item.files });
          }
        }
        failedUrlEntries += urlResult.failedItems.length;
        scenario.mark('download_urls_batch_completed', {
          batch_number: batchNumber,
          succeeded_entries: urlResult.succeededItems.length,
          failed_entries: urlResult.failedItems.length,
        });

        setProgress({
          current: Math.min(i + DOWNLOAD_BATCH_SIZE, allEntries.length),
          total: allEntries.length,
        });
      }

      if (abort.signal.aborted) {
        completeAbandoned();
        return;
      }

      scenario.mark('urls_fetched', {
        entries_with_urls: entryFileUrls.size,
        failed_entries: failedUrlEntries,
      });

      setStage('downloading');
      const zipEntries: { path: string; data: Uint8Array }[] = [];
      let downloadedFiles = 0;
      let failedFiles = 0;
      let totalFiles = 0;
      for (const { files } of entryFileUrls.values()) {
        totalFiles += Object.keys(files).length;
      }
      setProgress({ current: 0, total: totalFiles });
      scenario.mark('file_downloads_started', { total_files: totalFiles });

      const entryDetails = new Map<string, DataEntry>();
      for (const entry of allEntries) {
        entryDetails.set(entry.dataId, entry);
      }

      for (const [dataId, { entry, files }] of entryFileUrls) {
        const entryDetail = entryDetails.get(dataId);
        const entryName = sanitizeFileName(
          entryDetail?.name ?? entry.name ?? dataId,
        );

        let detailFiles: DataFile[] | undefined = entry.dataFiles;
        if (!detailFiles || detailFiles.length === 0) {
          try {
            const detail = await service.getDataEntry(
              dataSetId,
              dataId,
              downloadVersion,
            );
            detailFiles = detail.dataFiles;
          } catch {
            // fall through, use fileId as filename
          }
        }

        const fileIdToName = new Map<string, string>();
        if (detailFiles) {
          for (const df of detailFiles) {
            fileIdToName.set(df.id, df.fileName ?? df.id);
          }
        }

        for (const [fileId, url] of Object.entries(files)) {
          if (abort.signal.aborted) {
            completeAbandoned();
            return;
          }

          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.arrayBuffer();
            const fileName = sanitizeFileName(
              fileIdToName.get(fileId) ?? fileId,
            );
            zipEntries.push({
              path: `${entryName}/${fileName}`,
              data: new Uint8Array(blob),
            });
          } catch {
            failedFiles++;
          }

          downloadedFiles++;
          setProgress({ current: downloadedFiles, total: totalFiles });
        }
      }

      if (abort.signal.aborted) {
        completeAbandoned();
        return;
      }

      scenario.mark('files_downloaded', {
        downloaded_files: zipEntries.length,
        failed_files: failedFiles,
        total_files: totalFiles,
      });

      if (zipEntries.length === 0) {
        setErrorMessage('No files were downloaded.');
        setStage('error');
        scenario.complete('failure', { reason: 'no_files' });
        return;
      }

      setStage('building-zip');
      scenario.mark('zip_build_started', { file_count: zipEntries.length });
      const zipBlob = buildZipBlob(zipEntries);
      const url = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${sanitizeFileName(datasetName)}_v${downloadVersion}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      scenario.mark('zip_built', { file_count: zipEntries.length });

      setStage('done');
      if (failedUrlEntries > 0 || failedFiles > 0) {
        scenario.complete('failure', {
          reason: 'partial_download',
          entry_count: allEntries.length,
          downloaded_file_count: zipEntries.length,
          failed_file_count: failedFiles,
          failed_url_entry_count: failedUrlEntries,
        });
      } else {
        scenario.complete('success', {
          entry_count: allEntries.length,
          file_count: zipEntries.length,
        });
      }
    } catch (err) {
      if (!abort.signal.aborted) {
        setErrorMessage(
          err instanceof Error ? err.message : 'Download failed unexpectedly.',
        );
        setStage('error');
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
      } else {
        completeAbandoned();
      }
    }
  }, [service, dataSetId, selectedVersion, datasetName, scenarioFactory]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    setStage('confirm');
    setProgress({ current: 0, total: 0 });
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const isDownloading =
    stage === 'listing-entries' ||
    stage === 'getting-urls' ||
    stage === 'downloading' ||
    stage === 'building-zip';

  const versionLabel = selectedVersionInfo
    ? `v${selectedVersionInfo.version} · ${selectedVersionInfo.status}${selectedVersion === latestVersion ? ' (latest)' : ''}`
    : `v${selectedVersion}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open && !isDownloading) {
          handleClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Download Dataset</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {stage === 'confirm' && (
              <>
                <Body1>
                  Download all data entries and their files from &ldquo;
                  {datasetName}&rdquo; as a zip file.
                </Body1>
                {versionsLoading ? (
                  <Spinner size="tiny" label="Loading versions..." />
                ) : versions.length > 1 ? (
                  <Field label="Version">
                    <Dropdown
                      value={versionLabel}
                      selectedOptions={[String(selectedVersion)]}
                      onOptionSelect={handleVersionSelect}
                    >
                      {versions.map((v) => (
                        <Option
                          key={v.version}
                          value={String(v.version)}
                          text={`v${v.version} · ${v.status}${v.version === latestVersion ? ' (latest)' : ''}`}
                        >
                          v{v.version} ·{' '}
                          <Badge
                            appearance="filled"
                            color={statusColor(v.status)}
                            size="small"
                          >
                            {v.status}
                          </Badge>
                          {v.version === latestVersion ? ' (latest)' : ''}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                ) : null}
              </>
            )}

            {isDownloading && (
              <div className={styles.progressSection}>
                <Body1 className={styles.progressLabel}>
                  {STAGE_LABELS[stage]}
                </Body1>
                <ProgressBar
                  value={
                    progress.total > 0
                      ? progress.current / progress.total
                      : undefined
                  }
                />
                {progress.total > 0 && (
                  <Body1 className={styles.progressLabel}>
                    {progress.current} / {progress.total}
                  </Body1>
                )}
              </div>
            )}

            {stage === 'done' && (
              <Body1 className={styles.successText}>
                Dataset downloaded successfully.
              </Body1>
            )}

            {stage === 'error' && errorMessage !== null && (
              <Body1 className={styles.errorText}>{errorMessage}</Body1>
            )}
          </DialogContent>
          <DialogActions>
            {stage === 'confirm' && (
              <>
                <Button appearance="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  disabled={versionsLoading}
                  onClick={() => {
                    void handleDownload();
                  }}
                >
                  Download
                </Button>
              </>
            )}
            {isDownloading && (
              <Button appearance="secondary" onClick={handleClose}>
                Cancel
              </Button>
            )}
            {(stage === 'done' || stage === 'error') && (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
